import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { Validators } from '@angular/forms';
import { HandlerManager } from 'src/app/managers/handler/handler.manager';
import { LoadingManager } from 'src/app/managers/loading/loading.manager';
import { User } from 'src/app/beans/login/user/user.bean';
import { MatDialogRef } from '@angular/material/dialog';
import { LoginForcedDialog } from 'src/app/dialogs/login-forced/login-forced.dialog';
import { DialogsReturnCode } from 'src/app/enums/dialogs/returnCode.enum';
import { SessionManager } from 'src/app/managers/session/session.manager';
import {
  isSessionCode,
  SessionCode,
} from 'src/app/enums/session/session-code.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingPages } from 'src/app/enums/routing/routing-pages.enum';
import { Location } from '@angular/common';
import { TextVisibility } from 'src/app/enums/icons/password/textVisibility.enum';
import { CqExtraParamsDestination } from 'src/app/beans/components/cq-extra-params/cq-extra-params-destination.bean';

@Component({
  selector: 'landing',
  templateUrl: './landing.page.html',
  styleUrls: ['./landing.page.scss'],
})
export class LandingPage implements OnInit {
  // psw icon swap
  psw_visibility: TextVisibility = TextVisibility.HIDDEN;

  loginForm = new FormGroup({
    username: new FormControl('', Validators.required),
    password: new FormControl('', Validators.required),
  });

  codeForm = new FormGroup({
    code: new FormControl('', Validators.required),
  });

  orgDetail: any;
  hub: any;

  org_retrieved: boolean = false;
  hub_retrieved: boolean = false;

  // tenantId per il login esterno, letto da extraparam (type WAPP, code SSO_TENANT_ID)
  // scoped su org/hub. undefined = SSO non abilitato per questo org/hub, bottone nascosto.
  sso_tenant_id: string | undefined;

  // Profilo della sessione authproxy attiva (se esiste). undefined = nessuna sessione.
  sso_session: any | undefined;

  constructor(
    private _handler: HandlerManager,
    private _loader: LoadingManager,
    private _session: SessionManager,
    private _router: Router,
    private activatedRoute: ActivatedRoute,
    private _location: Location,
  ) {}

  async ngOnInit() {
    await this.checkOld();

    this.loginForm.setValue({
      username: '',
      password: '',
    });

    this.codeForm.setValue({
      code: '',
    });
  }

  async checkOld() {
    try {
      this.activatedRoute.queryParams.subscribe(async (params) => {
        let code = params['code']
          ? params['code']
          : params['hub']
            ? params['hub']
            : null;

        if (code) {
          if (code == ':code') code = null;
          this._router.navigate([RoutingPages.LANDING_RAW, code]);
        } else {
          this.initOrg();
        }
      });
    } catch (error) {}
  }

  // Initialize org
  initOrg() {
    this.activatedRoute.params.subscribe(async (params) => {
      this.hub = null;
      this.orgDetail = null;

      if (!params) {
        this.org_retrieved = true;
        this._router.navigate([RoutingPages.LANDING_RAW]);
        this._location.replaceState('');

        return;
      }

      let code = params['code'];

      if (code && code != ':code') {
        this.manageCode(code);
      } else {
        let _code = this._session.code().get();
        this._session.code().clear();

        if (!_code || _code == ':code')
          this._router.navigate([RoutingPages.LANDING_RAW]);

        if (!isSessionCode(_code))
          this._router.navigate([RoutingPages.LANDING_RAW, _code]);

        this._location.replaceState('');
      }
    });
    //
  }

  async manageCode(code: string) {
    //console.log(window.location);

    const { hostname, pathname } = window.location;

    const splitted = pathname.split('/');
    let trimmed = '';
    for (let i = 0; i < splitted.length - 1; i++) {
      let split = splitted[i];
      trimmed += split + '/';
    }

    const url = `${hostname}${trimmed}`;

    this._loader.start();
    const appRouter: any = await this._handler
      .appRouter()
      .getAppRouter('WAPP', null, url, code);
    this._loader.stop();
    if (!appRouter || !appRouter?.response) {
      this._location.replaceState('');
      return;
    }
    const router = appRouter?.response;
    switch (router.level) {
      case 'OR':
        this.manageOrganization(router, code);
        break;

      case 'HU':
        this.manageHubManager(router, code);
        break;
    }
    this._router.navigate([RoutingPages.LANDING_RAW, code]);
  }

  async manageHubManager(router: any, code: string) {
    if (!router) {
      this.hub = null;
      this.hub_retrieved = false;
      this.sso_tenant_id = undefined;
      return;
    }

    localStorage.clear();
    this.hub = { id: router.levelId, name: router.name, code };
    this.hub_retrieved = true;

    this._session.hub().set(JSON.stringify(this.hub));
    this._session.code().set(code);

    this.checkSso();
  }

  async manageOrganization(router: any, code: string) {
    if (!router) {
      this.orgDetail = null;
      this.org_retrieved = false;
      this.sso_tenant_id = undefined;
      return;
    }

    localStorage.clear();

    this.orgDetail = { id: router.levelId, name: router.name, code };
    this.org_retrieved = true;

    this._session.org().set(JSON.stringify(this.orgDetail));
    this._session.code().set(code);

    this.checkSso();
  }

  // Mostra il bottone SSO solo se esiste extraparam TENANT_ID (type SSO) per questo org/hub.
  async checkSso() {
    this.sso_tenant_id = undefined;
    this.sso_session = undefined;

    const level = this.hub ? 'HU' : this.orgDetail ? 'OR' : null;
    const levelId = this.hub?.id ?? this.orgDetail?.id;
    if (!level || !levelId) return;

    const dest: CqExtraParamsDestination = {
      level,
      levelid: levelId,
      type: 'SSO',
      codes: ['TENANT_ID'],
    };

    const resp: any = await this._handler
      .extraParams()
      .getParamByCode('', dest);
    this.sso_tenant_id = resp?.text1 || undefined;

    if (this.sso_tenant_id) await this.checkSsoSession();
  }

  // Sessione authproxy gia' attiva (login OIDC fatto in precedenza, non ancora scaduta/sloggata).
  async checkSsoSession() {
    this.sso_session = (await this._handler.sso().profile()) || undefined;
  }

  // ============== PSW BUTTON HANDLER ================

  switchVisibility() {
    if (this.psw_visibility == TextVisibility.HIDDEN)
      this.psw_visibility = TextVisibility.VISIBLE;
    else this.psw_visibility = TextVisibility.HIDDEN;
  }

  getPswType(): string {
    if (this.psw_visibility == TextVisibility.HIDDEN) return 'password';
    else return 'text';
  }

  // ==================================================

  async doLogin(forced: number) {
    if (this.loginForm.value && (this.orgDetail || this.hub)) {
      let username = this.loginForm.value.username;
      let psw = this.loginForm.value.password;

      this._loader.start();

      let response = await this._handler
        .login()
        .login(username, psw, this.orgDetail?.id, this.hub?.id, forced);

      this._loader.stop();

      if (!response) return;

      if (response instanceof MatDialogRef) {
        let dialog = response as MatDialogRef<LoginForcedDialog>;
        dialog.afterClosed().subscribe((data) => {
          if (data.code == DialogsReturnCode.LOGIN_FORCED_ACCEPT)
            this.doLogin(1);
        });
        return;
      }

      if (User.isKind(response)) {
        let user = response as User;
        let save = this._session.user().set(user);
        if (save == SessionCode.OK) {
          this._session.user().set(user);
          this._router.navigate([RoutingPages.MAIN]);
        }
      }
    }
  }

  // ==================================================

  async loginExternal() {
    if (!this.sso_tenant_id) return;

    this._loader.start();
    const returnUrl = `${window.location.origin}/${RoutingPages.SSO_CALLBACK}`;
    const response: any = await this._handler
      .sso()
      .start(this.sso_tenant_id, returnUrl);
    this._loader.stop();

    if (!response || !response.redirectUrl) return;

    window.location.href = response.redirectUrl;
  }

  // Sessione authproxy gia' presente: salta il giro OIDC, vai dritto alla pagina che
  // chiama legacy-login (stessa che gestisce gia' forced/errori sul ritorno da OIDC).
  loginExternalWithSession() {
    if (!this.sso_session) return;
    this._router.navigate([RoutingPages.SSO_CALLBACK]);
  }

  async logoutExternalSession() {
    if (!this.sso_tenant_id) return;

    this._loader.start();
    const result: any = await this._handler
      .sso()
      .logout(this.sso_tenant_id, window.location.href);
    this._loader.stop();
    this.sso_session = undefined;

    // Chiude anche la sessione sul provider esterno: va una navigazione reale, non fetch.
    if (result?.redirectUrl) window.location.href = result.redirectUrl;
  }

  async sendToUrl() {
    this._session.code().clear();
    const code = this.codeForm.get('code').value;

    const ciccio = await this._router.navigate([
      RoutingPages.LANDING_RAW,
      code,
    ]);

    if (ciccio === null)
      window.open(
        location.origin + '/' + RoutingPages.LANDING_RAW + '/' + code,
        '_self',
      );
  }
}
